/**
 * Main App Component
 *
 * Sets up routing and React Query provider
 */

import { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, CssBaseline } from '@mui/material';
// Use the approved Industry system as the base theme as well as inside the
// authenticated shell.  DashboardLayout still provides the scoped wrapper
// used by legacy page CSS, while this outer provider prevents public auth
// forms and portaled UI from falling back to the retired purple/Inter theme.
import theme from './industryTheme';
import { AuthProvider } from './context/AuthContext';
import { AIContextProvider } from './context/AIContext';
import AlphaCheckout from './pages/AlphaCheckout';
import Login from './pages/Login';
import BillingSuccess from './pages/BillingSuccess';
import BillingCancel from './pages/BillingCancel';
import Register from './pages/Register';
import ActivateAccount from './pages/ActivateAccount';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PublicTrialSignup from './pages/PublicTrialSignup';
import PublicTrialCheckout from './pages/PublicTrialCheckout';
import PublicTrialObserve from './pages/PublicTrialObserve';
import LoadingSpinner from './components/common/LoadingSpinner';
import GoogleAnalyticsRouteTracker from './components/GoogleAnalyticsRouteTracker';

const UnifiedLandingPage = lazy(() => import('./pages/UnifiedLandingPage'));
const UnifiedAboutPage = lazy(() => import('./pages/UnifiedAboutPage'));
const TestGlobeBackground = lazy(() => import('./pages/TestGlobeBackground'));
const ProtectedAppRoutes = lazy(() => import('./ProtectedAppRoutes'));
// Onboarding V2 alpha invitation flow. Lazy-loaded because it's only
// reachable from a one-time admin-issued invite link and would otherwise
// pull MUI form chunks into the public-landing critical path.
const OnboardingActivate = lazy(() => import('./pages/OnboardingActivate'));
// WhatsApp → iOS app hand-off page for the e-FRAT (zulbot:// bounce).
const OpenInAppEfrat = lazy(() => import('./pages/OpenInAppEfrat'));
const OpenInAppChecklist = lazy(() => import('./pages/OpenInAppChecklist'));

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <Router>
            <AIContextProvider>
              <GoogleAnalyticsRouteTracker />
              <Suspense fallback={<LoadingSpinner message="Carregando..." />}>
              <Routes>
              {/* Public Routes */}
              <Route path="/" element={<UnifiedLandingPage />} />
              <Route path="/about" element={<UnifiedAboutPage />} />
              <Route path="/landing" element={<Navigate to="/?theme=light" replace />} />
              <Route path="/whitepaper" element={<Navigate to="/" replace />} />
              <Route path="/alpha-trial/:accessKey" element={<AlphaCheckout />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/activate-account" element={<ActivateAccount />} />
              {/* Onboarding V2 alpha invitation flow (gated server-side
                  by ONBOARDING_V2_ENABLED; the route returns 404 from
                  the backend when the flag is off, which the page
                  renders as a friendly "invalid link" screen). */}
              <Route path="/onboarding/activate" element={<OnboardingActivate />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/billing/success" element={<BillingSuccess />} />
              <Route path="/billing/cancel" element={<BillingCancel />} />
              <Route path="/subscribe" element={<PublicTrialSignup />} />
              <Route path="/checkout" element={<PublicTrialCheckout />} />
              <Route path="/observe" element={<PublicTrialObserve />} />
              <Route path="/test-globe" element={<TestGlobeBackground />} />
              <Route path="/app/efrat" element={<OpenInAppEfrat />} />
              <Route path="/app/checklist" element={<OpenInAppChecklist />} />

              <Route path="/*" element={<ProtectedAppRoutes />} />
              </Routes>
              </Suspense>
            </AIContextProvider>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
